/**
 * Shared formatting for route metrics, so the comparison cards (RouteComparisonView) and the on-map
 * hover tooltips (RouteRenderManager) display exactly the same numbers and wording from one source.
 */

import { metricHelp } from "../RouteHelp";

export function formatKm(metres) {
  return `${(metres / 1000).toFixed(0)} km`;
}

export function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

export function formatDelta(value, formatter) {
  if (Math.round(value) === 0) {
    return "±0";
  }
  const sign = value > 0 ? "+" : "−";
  return `${sign}${formatter(Math.abs(value))}`;
}

/**
 * The one-line metrics row shared by the card and the tooltip (secure parkings, incidents, coverage,
 * largest gap).
 */
export function metricsLine(score, withHelp = false) {
  const coverage = Math.round(score.coverageScore * 100);
  const help = (label) => (withHelp ? metricHelp(label) : "");
  return `🅿️ ${score.psrCount}${help("Secure parkings")} · ⚠️ ${score.incidentNearCount}${help("Nearby incidents")} · Coverage ${coverage}%${help("Parking coverage")} · Max gap ${formatKm(score.maxGap)}${help("Max gap")}`;
}

/**
 * The "reference" / delta line shared by the card and the tooltip.
 *
 * @param {{score: object, isBaseline?: boolean}} option
 * @param {{score: object}} baseline
 */
export function deltaLine(option, baseline) {
  if (option.isBaseline) {
    return "Reference route";
  }
  const dDist = option.score.distance - baseline.score.distance;
  const dTime = option.score.duration - baseline.score.duration;
  return `Δ ${formatDelta(dDist, formatKm)} · Δ ${formatDelta(dTime, formatDuration)}`;
}

/**
 * HTML for the on-map hover tooltip: colour swatch + label, distance · time, delta line, metrics line.
 *
 * @param {{label: string, color: string, score: object, isBaseline?: boolean}} option
 * @param {{score: object}} baseline
 * @returns {string}
 */
export function tooltipHtml(option, baseline) {
  const s = option.score;
  return `
    <div style="min-width:190px;line-height:1.4;">
      <div style="display:flex;align-items:center;gap:6px;">
        <span style="display:inline-block;width:12px;height:12px;border-radius:2px;background:${option.color};"></span>
        <strong>${option.label}</strong>
      </div>
      <div>${formatKm(s.distance)} · ${formatDuration(s.duration)}</div>
      <div style="color:#6c757d;font-size:0.85em;">${deltaLine(option, baseline)}</div>
      <div style="font-size:0.85em;">${metricsLine(s)}</div>
    </div>
  `;
}
